// MediaDetail.js —
// - Médias normaux : layout "Pinterest" optimisé (+ édition latérale)
// - Vidéo finale (URL /mediaDetails/final-<id>) : layout projet + liste des médias utilisés

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
  useState
} from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";


/* ============================================================================
 * VIDEO: autoplay only when the main video is "in focus"
 * - Focus = visible in viewport (intersection ratio >= 0.6) AND tab/window is active
 * - Pauses immediately when out of focus
 * ========================================================================== */

function useVideoAutoPlayOnFocus(videoRef, enabled, srcKey, { minRatio = 0.6 } = {}) {
  const userPausedRef = useRef(false);
  const userChangedAudioRef = useRef(false);
  const mutedByUsRef = useRef(false);

  useEffect(() => {
    const v = videoRef?.current;
    if (!v) return;

    // Reset flags when src changes
    userPausedRef.current = false;
    userChangedAudioRef.current = false;
    mutedByUsRef.current = false;

    const state = {
      inView: false,
      docVisible: typeof document !== "undefined" ? document.visibilityState === "visible" : true,
      winFocused:
        typeof document !== "undefined" && typeof document.hasFocus === "function"
          ? document.hasFocus()
          : true,
    };

    const shouldPlay = () => !!enabled && state.inView && state.docVisible && state.winFocused;

    const safePause = () => {
      try {
        v.pause();
      } catch { }
    };

    const tryPlay = async () => {
      if (!shouldPlay()) {
        // leaving focus: stop and allow autoplay next time we come back
        userPausedRef.current = false;
        safePause();
        return;
      }
      if (userPausedRef.current) return;

      // already playing
      if (!v.paused && !v.ended) return;

      try {
        const p = v.play();
        if (p && typeof p.then === "function") await p;
      } catch (e) {
        // Autoplay policy fallback (often blocks unmuted autoplay)
        if (!v.muted && !userChangedAudioRef.current) {
          try {
            v.muted = true;
            mutedByUsRef.current = true;
            const p2 = v.play();
            if (p2 && typeof p2.then === "function") await p2;
          } catch { }
        }
      }
    };

    const onPause = () => {
      // If user pauses while focused, respect it until focus is lost and regained.
      if (shouldPlay()) userPausedRef.current = true;
    };
    const onPlay = () => {
      userPausedRef.current = false;
    };
    const onVolumeChange = () => {
      userChangedAudioRef.current = true;
    };

    v.addEventListener("pause", onPause);
    v.addEventListener("play", onPlay);
    v.addEventListener("volumechange", onVolumeChange);

    let obs = null;

    const thresholds = Array.from(
      new Set([0, 0.25, 0.5, minRatio, 0.75, 1].filter((x) => x >= 0 && x <= 1))
    ).sort((a, b) => a - b);

    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      obs = new IntersectionObserver(
        (entries) => {
          const entry = entries?.[0];
          if (!entry) return;
          const ratio = entry.intersectionRatio || 0;
          state.inView = !!entry.isIntersecting && ratio >= minRatio;
          tryPlay();
        },
        { threshold: thresholds }
      );
      obs.observe(v);
    } else {
      // Fallback: treat as in view
      state.inView = true;
    }

    const onVisibility = () => {
      state.docVisible = document.visibilityState === "visible";
      tryPlay();
    };
    const onWinFocus = () => {
      state.winFocused = true;
      tryPlay();
    };
    const onWinBlur = () => {
      state.winFocused = false;
      tryPlay();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onWinFocus);
    window.addEventListener("blur", onWinBlur);

    // initial attempt after first paint
    requestAnimationFrame(() => {
      tryPlay();
    });

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onWinFocus);
      window.removeEventListener("blur", onWinBlur);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("volumechange", onVolumeChange);
      if (obs) obs.disconnect();
      safePause();
    };
  }, [enabled, srcKey, minRatio, videoRef]);
}


/* ============================================================================
 * I18N (same logic as LandingPage.js)
 * - Language is set in SettingsPage and stored in localStorage("weavevo_lang")
 * - Fallback: localStorage("weavevo_language")
 * ========================================================================== */

const MD_LANG_KEY = "weavevo_lang";
const MD_LANG_FALLBACK_KEY = "weavevo_language";

function safeReadLang(storageKey = MD_LANG_KEY) {
  try {
    const raw =
      localStorage.getItem(storageKey) ||
      localStorage.getItem(MD_LANG_FALLBACK_KEY) ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

function safeEnsureLang(next, storageKey = MD_LANG_KEY) {
  try {
    const raw =
      localStorage.getItem(storageKey) ||
      localStorage.getItem(MD_LANG_FALLBACK_KEY) ||
      "";
    if (!raw) localStorage.setItem(storageKey, next);
  } catch { }
}

const MD_I18N = {
  en: {
    // common
    common_loading: "Loading",
    common_loading_ellipsis: "Loading…",
    common_cancel: "Cancel",
    common_retry: "Retry",
    common_title: "Title",
    common_description: "Description",
    common_location_context: "Location / context",
    common_open_new_tab: "Open in new tab",
    common_share: "Share",
    common_like: "Like",
    common_liked_by: "Liked by",
    common_no_likes_yet: "No likes yet",
    common_actions: "Actions",
    common_comments: "Comments",
    common_contributors: "Contributors",
    common_creator: "Creator",
    common_owner: "Owner",
    common_add_comment_ph: "Add a comment…",


    common_send: "Send",

    err_add_comment: "Error while adding the comment.",
    err_like_update: "Error while updating the like.",
    err_load_likes: "Unable to load likes.",
    err_delete_media: "Unable to delete this media.",
    // media detail
    md_not_found: "Media not found",
    md_edit_info: "Edit info",
    md_edit_info_alt: "Edit info",
    md_trim_video: "Trim video",
    md_delete: "Delete",
    md_delete_media: "Delete this media",
    md_publish: "Publish",
    md_edit_media: "Edit this media",
    md_start_seconds: "Start (seconds)",
    md_end_seconds: "End (seconds)",
    md_view_project_recap: "View project recap",
    md_view_project_recap_arrow: "View project recap →",

    // placeholders
    md_title_ph: "Media title",
    md_desc_ph: "Tell what this media shows…",
    md_location_ph: "e.g. Beijing, Great Wall of China",

    // toasts / messages
    toast_comment_added: "Comment added!",
    toast_info_updated: "Information updated.",
    toast_trim_ok: "New trimmed version generated.",
    toast_trim_invalid: "Invalid range. Check the start and end.",
    toast_link_copied: "Link copied",
    toast_copy_failed: "Copy failed",
    toast_media_deleted: "Media deleted.",
    toast_login_like_media: "Please log in to like this media.",
    toast_login_like_video: "Please log in to like this video.",
    toast_login_comment_media: "Please log in to comment on this media.",
    toast_login_comment: "Please log in to comment.",
    toast_login_edit: "Please log in to edit this media.",
    toast_login_delete: "Please log in to delete this media.",
    toast_owner_only: "Only the owner can edit or delete this media.",
    toast_close: "Close notification",
    toast_close_panel: "Close panel",
  },
  fr: {
    // common
    common_loading: "Chargement",
    common_loading_ellipsis: "Chargement…",
    common_cancel: "Annuler",
    common_retry: "Réessayer",
    common_title: "Titre",
    common_description: "Description",
    common_location_context: "Lieu / contexte",
    common_open_new_tab: "Ouvrir dans un onglet",
    common_share: "Partager",
    common_like: "J'aime",
    common_liked_by: "Aimé par",
    common_no_likes_yet: "Aucun like pour le moment",
    common_actions: "Actions",
    common_comments: "Commentaires",
    common_contributors: "Contributeurs",
    common_creator: "Créateur",
    common_owner: "Propriétaire",
    common_add_comment_ph: "Ajouter un commentaire…",


    common_send: "Envoyer",

    err_add_comment: "Erreur lors de l\'ajout du commentaire.",
    err_like_update: "Erreur lors de la mise à jour du like.",
    err_load_likes: "Impossible de charger la liste des likes.",
    err_delete_media: "Impossible de supprimer ce média.",
    // media detail
    md_not_found: "Média introuvable",
    md_edit_info: "Modifier les infos",
    md_edit_info_alt: "Éditer les infos",
    md_trim_video: "Raccourcir la vidéo",
    md_delete: "Supprimer",
    md_delete_media: "Supprimer ce média",
    md_publish: "Publier",
    md_edit_media: "Modifier ce média",
    md_start_seconds: "Début (secondes)",
    md_end_seconds: "Fin (secondes)",
    md_view_project_recap: "Voir le récap du projet",
    md_view_project_recap_arrow: "Voir le récap du projet →",

    // placeholders
    md_title_ph: "Titre du média",
    md_desc_ph: "Racontez ce que montre ce média…",
    md_location_ph: "Ex: Pékin, muraille de Chine",

    // toasts / messages
    toast_comment_added: "Commentaire ajouté !",
    toast_info_updated: "Informations mises à jour.",
    toast_trim_ok: "Nouvelle version recadrée générée.",
    toast_trim_invalid: "Plage invalide. Vérifiez le début et la fin.",
    toast_link_copied: "Lien copié",
    toast_copy_failed: "Échec copie",
    toast_media_deleted: "Média supprimé.",
    toast_login_like_media: "Connectez-vous pour aimer ce média.",
    toast_login_like_video: "Connectez-vous pour aimer cette vidéo.",
    toast_login_comment_media: "Connectez-vous pour commenter ce média.",
    toast_login_comment: "Connectez-vous pour commenter.",
    toast_login_edit: "Connectez-vous pour modifier ce média.",
    toast_login_delete: "Connectez-vous pour supprimer ce média.",
    toast_owner_only: "Seul le propriétaire peut modifier ou supprimer ce média.",
    toast_close: "Fermer la notification",
    toast_close_panel: "Fermer le panneau",
  },
};

function useWeavevoI18n(dict, storageKey = MD_LANG_KEY) {
  const location = useLocation();
  const [lang, setLang] = useState(() => safeReadLang(storageKey));
  useEffect(() => {
    const read = () => {
      const next = safeReadLang(storageKey);
      setLang(next);
      safeEnsureLang(next, storageKey);
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);

    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, [storageKey]);

  // If language is changed from Settings page, we often navigate after saving;
  // re-read language on navigation to refresh immediately.
  useEffect(() => {
    const next = safeReadLang(storageKey);
    if (next !== lang) setLang(next);
  }, [location.pathname, location.search, lang, storageKey]);

  useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch { }
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const dictForLang = dict?.[lang] || dict?.en || {};
      let out = dictForLang[key] ?? dict?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [lang, dict]
  );

  const locale = lang === "fr" ? "fr-FR" : "en-US";
  const tt = useCallback((fr, en) => (lang === "fr" ? fr : en), [lang]);

  return { lang, locale, t, tt };
}


/* ============================================================================
 * 1) OUTILS COMMUNS
 * ========================================================================== */

/**
 * Petit helper pour deviner le type de fichier
 */
function isVideoFile(path = "") {
  const ext = path.split(".").pop()?.toLowerCase();
  return ["mp4", "webm", "mov", "ogg", "mkv"].includes(ext);
}
function isImageFile(path = "") {
  const ext = path.split(".").pop()?.toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "avif"].includes(ext);
}

/**
 * Helpers auth (frontend-only): extract current user id from JWT token
 * so we can restrict edit/delete UI to the owner of the media.
 */
function base64UrlToBase64(input = "") {
  return String(input).replace(/-/g, "+").replace(/_/g, "/");
}

function safeDecodeJwtPayload(token) {
  try {
    if (!token) return null;
    const parts = String(token).split(".");
    if (parts.length < 2) return null;
    const b64 = base64UrlToBase64(parts[1]);
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    try {
      // fallback (non-unicode payload)
      const parts = String(token || "").split(".");
      if (parts.length < 2) return null;
      const b64 = base64UrlToBase64(parts[1]);
      const padded = b64 + "===".slice((b64.length + 3) % 4);
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }
}

function normalizeUserId(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  return v.includes("@") ? v.split("@")[0] : v;
}

function extractUserIdFromToken(token) {
  const p = safeDecodeJwtPayload(token);
  const raw =
    p?.preferred_username ||
    p?.username ||
    p?.email ||
    p?.upn ||
    p?.name ||
    "";
  return normalizeUserId(raw);
}


/**
 * Fetch léger d’un pool de médias (max ~80) :
 * - si on a un token : /media/me?limit=80
 * - sinon : /media?limit=80
 */
async function fetchLightMediaPool(token) {
  const MAX_POOL = 80;
  const hasToken = !!token;
  const headers = hasToken ? { Authorization: `Bearer ${token}` } : {};
  const baseUrl = hasToken ? "/media/me?limit=80" : "/media?limit=80";

  try {
    const r = await fetch(baseUrl, { headers });
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const txt = await r.text();
      console.warn("[MediaDetail] pool non JSON:", txt.slice(0, 200));
      return [];
    }
    const j = await r.json();
    const arr = Array.isArray(j)
      ? j
      : Array.isArray(j?.items)
        ? j.items
        : Array.isArray(j?.data)
          ? j.data
          : Array.isArray(j?.results)
            ? j.results
            : Array.isArray(j?.rows)
              ? j.rows
              : [];
    return arr.slice(0, MAX_POOL);
  } catch (e) {
    console.error("[MediaDetail] Erreur pool:", e);
    return [];
  }
}

/* ============================================================================
 * 2) LAYOUT “PINTEREST” — MÉDIAS NORMAUX
 *    -> Pool allégé + panneau de modification rapide à droite
 * ========================================================================== */

async function fetchAllUserMediaLite(token) {
  return fetchLightMediaPool(token);
}

function LegacyMediaDetails({ t = (k) => k, lang = "en", locale = "en-US" }) {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [media, setMedia] = useState(null);
  const [allPool, setAllPool] = useState([]); // every item from /media(/me)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // édition champs texte
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  // suppression
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // recadrage vidéo
  const [trimDuration, setTrimDuration] = useState(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [savingTrim, setSavingTrim] = useState(false);
  const videoRef = useRef(null);

  // topbar + commentaires
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = useRef(null);
  const commentsRef = useRef(null);

  const editInlineRef = useRef(null);
  const [editInlineOpen, setEditInlineOpen] = useState(false);

  // ✅ trim inline sous les commentaires
  const trimInlineRef = useRef(null);
  const [trimInlineOpen, setTrimInlineOpen] = useState(false);

  const currentUserId = useMemo(() => extractUserIdFromToken(token), [token]);

  // ✅ keep a "real" username (not normalized) for avatars/profile links
  const currentUsername = useMemo(() => {
    const p = safeDecodeJwtPayload(token);
    const raw =
      p?.preferred_username ||
      p?.username ||
      p?.email ||
      p?.upn ||
      p?.name ||
      "";
    const s = String(raw || "").trim();
    if (s.includes("@")) return s.split("@")[0];
    return s || currentUserId || "";
  }, [token, currentUserId]);

  const ownerUserId = useMemo(() => {
    // backend typically exposes the owner as `user` (username)
    return normalizeUserId(media?.user || media?.created_by || media?.owner || "");
  }, [media]);


  const isOwner = useMemo(() => {
    return !!currentUserId && !!ownerUserId && currentUserId === ownerUserId;
  }, [currentUserId, ownerUserId]);

  // Project-media uses its own routes/table. Never send edit/delete/trim actions for
  // PROJECT_MEDIA to /media/:id just because the current user is the contributor.
  const canEditPersonalMedia = useMemo(() => {
    const src = String(media?.sourceKind || media?.source || "MEDIA").toUpperCase();
    const isProjectMedia = src === "PROJECT_MEDIA" || src === "PROJECT";
    return !!token && !!isOwner && !isProjectMedia && media?.canEdit !== false;
  }, [media, token, isOwner]);

  const openInlineEdit = () => {
    if (!token || !canEditPersonalMedia) {
      setShowActionsMenu(false);
      return;
    }

    setCommentsExpanded(true);
    setEditInlineOpen(true);
    setTrimInlineOpen(false); // ✅ fermer trim si ouvert
    setPanelMode(null);
    setShowDeleteConfirm(false);
    setShowActionsMenu(false);

    try {
      window.history.replaceState(null, "", `${window.location.pathname}#edit`);
    } catch { }

    requestAnimationFrame(() => {
      if (editInlineRef.current) {
        editInlineRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (commentsRef.current) {
        commentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const openInlineTrim = () => {
    if (!token || !canEditPersonalMedia) {
      setShowActionsMenu(false);
      return;
    }

    setCommentsExpanded(true);
    setTrimInlineOpen(true);
    setEditInlineOpen(false); // ✅ fermer edit si ouvert
    setPanelMode(null);
    setShowDeleteConfirm(false);
    setShowActionsMenu(false);

    try {
      window.history.replaceState(null, "", `${window.location.pathname}#trim`);
    } catch { }

    requestAnimationFrame(() => {
      if (trimInlineRef.current) {
        trimInlineRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (commentsRef.current) {
        commentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };



  const [comments, setComments] = useState([]);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [reactionsLoading, setReactionsLoading] = useState(false);

  // Likes list popover (same UX as Media likes)
  const [likesPopoverOpen, setLikesPopoverOpen] = useState(false);
  const [likesUsers, setLikesUsers] = useState(null); // null = not loaded yet
  const [likesUsersLoading, setLikesUsersLoading] = useState(false);
  const [likesUsersError, setLikesUsersError] = useState(null);


  // -------------------------
  // OWNER AVATAR (real from DB) — UI only
  // -------------------------
  const [legacyUsersByUsername, setLegacyUsersByUsername] = useState({});
  const [legacyAvatarBust, setLegacyAvatarBust] = useState(0);

  useEffect(() => {
    if (!token) return;

    let alive = true;

    fetch("/users", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) return [];
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return [];
        return r.json();
      })
      .then((arr) => {
        if (!alive) return;
        const map = {};
        (arr || []).forEach((u) => {
          if (u?.username) map[u.username] = u;
        });
        setLegacyUsersByUsername(map);
        setLegacyAvatarBust(Date.now());
      })
      .catch(() => {
        if (!alive) return;
        setLegacyUsersByUsername({});
      });

    return () => {
      alive = false;
    };
  }, [token]);

  const legacyNormalizeUploadPath = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const cleaned = raw.replace(/^\/?uploads\/?/i, "");
    return `/uploads/${cleaned}`;
  };

  const legacyToInitials = (nameOrUsername) => {
    const raw = String(nameOrUsername || "").trim();
    if (!raw) return "?";
    const base = raw.includes("@") ? raw.split("@")[0] : raw;
    const parts = base.split(/[^a-zA-Z0-9]+/g).filter(Boolean);
    const first = parts[0]?.charAt(0) || base.charAt(0) || "?";
    const second = parts[1]?.charAt(0) || "";
    return (first + second).toUpperCase();
  };

  const legacyMakeUiAvatar = (label) => {
    const initials = legacyToInitials(label);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=0F172A&color=ffffff&rounded=true&size=80`;
  };

  const legacyAppendBust = (src) => {
    if (!src) return src;
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}v=${legacyAvatarBust}`;
  };

  const legacyAvatarSrcForUsername = (username) => {
    const u = String(username || "").trim();
    if (!u) return legacyMakeUiAvatar("?");
    const lower = u.toLowerCase();
    const rec =
      legacyUsersByUsername?.[u] ||
      legacyUsersByUsername?.[lower] ||
      legacyUsersByUsername?.[normalizeUserId(u)] ||
      null;


    const cand =
      rec?.profilePhoto ||
      rec?.profile_photo ||
      rec?.photo ||
      rec?.photoUrl ||
      rec?.photo_url ||
      rec?.avatar ||
      rec?.avatarUrl ||
      rec?.avatar_url ||
      rec?.picture ||
      rec?.image ||
      rec?.imageUrl ||
      rec?.image_url ||
      null;

    const resolved = legacyNormalizeUploadPath(cand) || legacyMakeUiAvatar(u);
    return legacyAppendBust(resolved);
  };

  const legacyResolveUserProfilePath = (username) => {
    const u = String(username || "").trim();
    if (!u) return null;
    return `/users/${encodeURIComponent(u)}`;
  };



  // palette latérale : "meta" | "trim" | null
  const [panelMode, setPanelMode] = useState(null);

  // layout pinterest
  const [queue, setQueue] = useState([]);
  const [rightSim, setRightSim] = useState([]);
  const [bottomSim, setBottomSim] = useState([]);

  const mainCardRef = useRef(null);
  const rightRailRef = useRef(null);
  const tryFillRef = useRef(null);

  // toast
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = (type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, 3200);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);


  // If this is not an editable personal media, ensure edit/delete UIs are closed
  useEffect(() => {
    if (!media) return;
    if (!canEditPersonalMedia) {
      setEditInlineOpen(false);
      setTrimInlineOpen(false);
      setPanelMode(null);
      setShowDeleteConfirm(false);
      setShowActionsMenu(false);
    }
  }, [media, canEditPersonalMedia]);

  // fermeture du menu "..." quand on clique en dehors
  useEffect(() => {
    const onClickOutside = (ev) => {
      if (!actionsMenuRef.current) return;
      if (!actionsMenuRef.current.contains(ev.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleScrollToComments = () => {
    if (!commentsRef.current) return;
    commentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getReactionsEndpoints = (m) => {
    const src = String(m?.source || "MEDIA").toUpperCase();
    if (src === "PROJECT_MEDIA" || src === "PROJECT") {
      return {
        likeUrl: `/project-media/${m.id}/like`,
        commentsUrl: `/project-media/${m.id}/comments`,
        likesListUrl: `/project-media/${m.id}/likes`,
      };

      // Reset likes popover cache when switching media
      useEffect(() => {
        setLikesPopoverOpen(false);
        setLikesUsers(null);
        setLikesUsersError(null);
      }, [media?.id, media?.source]);

    }
    return {
      likeUrl: `/media/${m.id}/like`,
      commentsUrl: `/media/${m.id}/comments`,
      likesListUrl: `/media/${m.id}/likes`,
    };
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text) return;
    if (!media) return;

    if (!token) {
      showToast("info", t("toast_login_comment_media"));
      return;
    }

    const { commentsUrl } = getReactionsEndpoints(media);

    try {
      const resp = await fetch(commentsUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text }),
      });

      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const txt = await resp.text();
        console.error("[MediaDetail] Réponse non JSON pour POST comments", txt.slice(0, 200));
        throw new Error("Réponse serveur invalide lors de l'ajout du commentaire.");
      }

      const data = await resp.json();
      if (!resp.ok || data.error) {
        throw new Error(data.error || "Impossible d'ajouter le commentaire.");
      }

      const raw = data.comment || {};
      const newCommentObj = {
        id: raw.id ?? Date.now(),
        author: String(
          raw.author ||
          raw.user ||
          raw.username ||
          currentUsername ||
          currentUserId ||
          "Moi"
        ).trim() || "Moi",
        text: raw.text ?? text,
        createdAt: raw.createdAt || raw.created_at || new Date().toISOString(),
      };


      setComments((prev) => [newCommentObj, ...prev]);
      setNewComment("");
    } catch (error) {
      console.error("[MediaDetail] Erreur ajout commentaire:", error);
      showToast("error", error.message || t("err_add_comment"));
    }

  };

  const fetchFinalLikesUsers = async () => {
    if (!projectId) return;

    try {
      setLikesUsersLoading(true);
      setLikesUsersError(null);

      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const resp = await fetch(`/projects/${projectId}/final/likes`, { headers });

      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const txt = await resp.text();
        throw new Error((txt || "").slice(0, 140) || t("err_load_likes"));
      }

      const data = await resp.json();

      const items =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.likes) && data.likes) ||
        (Array.isArray(data?.users) && data.users) ||
        [];

      setLikesUsers(items);
    } catch (e) {
      console.error("[FinalVideoDetails] Error loading likes:", e);
      setLikesUsersError(t("err_load_likes"));
      setLikesUsers([]);
    } finally {
      setLikesUsersLoading(false);
    }
  };

  const handleToggleLike = async () => {
    if (!media) return;

    if (!token) {
      showToast("info", t("toast_login_like_media"));
      return;
    }

    const { likeUrl } = getReactionsEndpoints(media);

    try {
      setReactionsLoading(true);

      const resp = await fetch(likeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        const txt = await resp.text();
        console.error("[MediaDetail] Réponse non JSON pour POST like", txt.slice(0, 200));
        throw new Error("Réponse serveur invalide lors de la mise à jour du like.");
      }

      const data = await resp.json();
      if (!resp.ok || data.error) {
        throw new Error(data.error || "Impossible de mettre à jour le like.");
      }

      if (typeof data.likes_count === "number") setLikesCount(data.likes_count);
      if (typeof data.user_has_liked === "boolean") setHasLiked(data.user_has_liked);

      // Keep likes list (if already loaded) in sync with toggle action
      if (Array.isArray(likesUsers) && currentUserId) {
        const me = String(currentUserId || "").trim();
        if (me) {
          setLikesUsers((prev) => {
            if (!Array.isArray(prev)) return prev;
            const exists = prev.some((u) => String(u?.username || "").toLowerCase() === me.toLowerCase());
            if (data.user_has_liked) {
              return exists ? prev : [{ username: me }, ...prev];
            }
            return exists ? prev.filter((u) => String(u?.username || "").toLowerCase() !== me.toLowerCase()) : prev;
          });
        }
      }
    } catch (error) {
      console.error("[MediaDetail] Erreur toggle like:", error);
      showToast("error", error.message || t("err_like_update"));
    } finally {
      setReactionsLoading(false);
    }
  };


  const fetchLikesUsers = useCallback(async () => {
    if (!media) return;

    const { likesListUrl } = getReactionsEndpoints(media);
    if (!likesListUrl) return;

    try {
      setLikesUsersLoading(true);
      setLikesUsersError(null);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(likesListUrl, { headers });

      const ct = resp.headers.get("content-type") || "";
      let data = null;

      if (ct.includes("application/json")) {
        data = await resp.json();
      } else {
        const txt = await resp.text();
        throw new Error((txt || "").slice(0, 140) || t("err_load_likes"));
      }

      if (!resp.ok || data?.error) {
        throw new Error(data?.error || t("err_load_likes"));
      }

      let arr =
        (Array.isArray(data) && data) ||
        (Array.isArray(data?.users) && data.users) ||
        (Array.isArray(data?.likes) && data.likes) ||
        (Array.isArray(data?.likers) && data.likers) ||
        (Array.isArray(data?.data) && data.data) ||
        [];

      const normalized = arr
        .map((u) => {
          if (!u) return null;
          if (typeof u === "string") return { username: String(u).trim() };

          const username =
            u.username ||
            u.user ||
            u.author ||
            u.created_by ||
            u.createdBy ||
            u.owner ||
            u.name ||
            u.email ||
            u.id ||
            "";

          const displayName =
            u.displayName ||
            u.display_name ||
            u.fullName ||
            u.full_name ||
            u.name ||
            null;

          const un = String(username || "").trim();
          if (!un) return null;
          return {
            username: un,
            displayName: displayName ? String(displayName) : null,
            raw: u,
          };
        })
        .filter(Boolean);

      // de-dup by username (case-insensitive)
      const seen = new Set();
      const uniq = [];
      for (const u of normalized) {
        const k = String(u.username || "").toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        uniq.push(u);
      }

      setLikesUsers(uniq);
    } catch (error) {
      console.error("[MediaDetail] Erreur chargement likes:", error);
      setLikesUsers([]);
      const msg = String(error?.message || "").trim();
      const isGeneric = !msg || /failed to load likes/i.test(msg) || /unable to load likes/i.test(msg) || /sqlite_error/i.test(msg) || /no such column/i.test(msg);
      setLikesUsersError(isGeneric ? t("err_load_likes") : msg);

    } finally {
      setLikesUsersLoading(false);
    }
  }, [media, token, t]);




  const openPanel = (mode) => {
    if (!token) {
      showToast("info", t("toast_login_edit"));
      return;
    }
    if (!canEditPersonalMedia) {
      showToast("error", t("toast_owner_only"));
      return;
    }
    setPanelMode(mode);
  };
  const closePanel = () => {
    setPanelMode(null);
    setShowDeleteConfirm(false);
  };

  /** Load current item + pool léger */
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setErr(null);
    setPanelMode(null);
    setShowDeleteConfirm(false);
    setComments([]);
    setNewComment("");

    // 1) média principal via GET /mediaDetailsData/:id
    (async () => {
      try {
        const headers = {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const resp = await fetch(`/mediaDetailsData/${id}`, {
          method: "GET",
          headers,
        });

        const ct = resp.headers.get("content-type") || "";

        if (!ct.includes("application/json")) {
          const txt = await resp.text();
          console.error(
            `[MediaDetail] Réponse non JSON pour GET /mediaDetailsData/${id} (status=${resp.status}, type=${ct})`,
            txt.slice(0, 200)
          );

          if (resp.status === 404) {
            throw new Error("Ce média n'existe plus ou a été supprimé.");
          }

          throw new Error(
            "Impossible de charger ce média (réponse serveur invalide)."
          );
        }

        const data = await resp.json();

        if (!resp.ok || !data || data.error) {
          throw new Error(
            data?.error ||
            (resp.status === 404
              ? "Ce média n'existe plus ou a été supprimé."
              : `Erreur serveur (${resp.status}) lors du chargement du média.`)
          );
        }

        setMedia(data);
      } catch (e) {
        console.error("[MediaDetail] Erreur chargement GET /mediaDetailsData", e);
        setErr(e.message || "Erreur de chargement du média.");
      } finally {
        setLoading(false);
      }
    })();

    // 2) pool /media(/me) allégé
    (async () => {
      try {
        const all = await fetchAllUserMediaLite(token);
        all.sort((a, b) => {
          const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        });
        setAllPool(all);
      } catch (e) {
        console.error("[MediaDetail] Erreur pool /media(/me):", e);
      }
    })();
  }, [id, token]);

  // initialiser les champs d'édition quand le média est chargé
  useEffect(() => {
    if (!media) return;
    setEditTitle(media.title || "");
    setEditDescription(media.description || "");
    setEditLocation(media.location || "");

    if (typeof media.likes_count === "number") {
      setLikesCount(media.likes_count);
    } else {
      setLikesCount(0);
    }

    if (typeof media.user_has_liked === "boolean") {
      setHasLiked(media.user_has_liked);
    } else {
      setHasLiked(false);
    }
  }, [media]);


  useEffect(() => {
    if (!media) return;

    let cancelled = false;

    (async () => {
      try {
        const headers = {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const { commentsUrl } = getReactionsEndpoints(media);

        const resp = await fetch(commentsUrl, {
          method: "GET",
          headers,
        });

        const ct = resp.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const txt = await resp.text();
          console.error("[MediaDetail] Réponse non JSON pour GET comments", txt.slice(0, 200));
          return;
        }

        const payload = await resp.json();
        const arr = Array.isArray(payload?.comments)
          ? payload.comments
          : Array.isArray(payload)
            ? payload
            : [];

        const mapped = arr.map((c) => ({
          ...c,
          createdAt: c.createdAt || c.created_at,
        }));

        if (!cancelled) setComments(mapped);
      } catch (e) {
        console.error("[MediaDetail] Erreur chargement commentaires:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [media, token]);



  // Candidates sont exactement ce que renvoie /media(/me)
  const candidates = useMemo(
    () => (Array.isArray(allPool) ? allPool : []),
    [allPool]
  );

  // Reset layout queues whenever pool changes
  useEffect(() => {
    setRightSim([]);
    setBottomSim([]);
    setQueue(candidates);
  }, [candidates]);

  // Fill RIGHT column until its height >= main height, then overflow everything else to BOTTOM
  const tryFillRight = () => {
    const mainEl = mainCardRef.current;
    const rightEl = rightRailRef.current;
    if (!mainEl || !rightEl) return;

    const mainH = mainEl.getBoundingClientRect().height;
    const rightH = rightEl.getBoundingClientRect().height;
    const tolerance = 8;

    if (queue.length === 0) return;

    if (rightH + tolerance < mainH) {
      setRightSim((prev) => (queue.length ? [...prev, queue[0]] : prev));
      setQueue((prev) => prev.slice(1));
    } else {
      setBottomSim(queue);
      setQueue([]);
    }
  };

  tryFillRef.current = tryFillRight;

  // On window resize, repack (put everything back into queue, then refill)
  useLayoutEffect(() => {
    const onResize = () => {
      setQueue((prevQueue) => {
        const combined = [...rightSim, ...bottomSim, ...prevQueue];
        setRightSim([]);
        setBottomSim([]);
        return combined;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [rightSim, bottomSim]);

  // After each paint/update of rightSim/queue, try to fill again
  useEffect(() => {
    const raf = requestAnimationFrame(
      () => tryFillRef.current && tryFillRef.current()
    );
    return () => cancelAnimationFrame(raf);
  }, [rightSim, queue]);

  // --- Actions: mise à jour des métadonnées ---
  const handleSaveMeta = async (e) => {
    e.preventDefault();
    if (!media) return;
    if (!token) {
      showToast("info", t("toast_login_edit"));
      return;
    }
    if (!canEditPersonalMedia) {
      showToast("error", t("toast_owner_only"));
      return;
    }
    setSavingMeta(true);
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const payload = {
        title: editTitle,
        description: editDescription,
        location: editLocation,
      };
      const r = await fetch(`/media/${media.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `Erreur ${r.status}`);
      }
      let updated = null;
      try {
        updated = await r.json();
      } catch {
        // backend peut ne rien renvoyer, on garde alors payload
      }
      setMedia((prev) => ({
        ...(prev || {}),
        ...payload,
        ...(updated || {}),
      }));
      showToast("success", t("toast_info_updated"));
      closePanel();
    } catch (e2) {
      console.error("[MediaDetail] Erreur update meta:", e2);
      showToast(
        "error",
        e2.message || "Erreur lors de la mise à jour du média."
      );
    } finally {
      setSavingMeta(false);
    }
  };

  // --- Action: suppression du média ---
  const handleDeleteMedia = async () => {
    if (!media || deleting) return;
    if (!token) {
      showToast("info", t("toast_login_delete"));
      return;
    }
    if (!canEditPersonalMedia) {
      showToast("error", t("toast_owner_only"));
      return;
    }
    setDeleting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await fetch(`/media/${media.id}`, {
        method: "DELETE",
        headers,
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `Erreur ${r.status}`);
      }
      showToast("success", t("toast_media_deleted"));

      // choisir un autre média à afficher dans les détails
      const others = (allPool || []).filter(
        (m) => m && m.id && m.id !== media.id
      );

      setTimeout(() => {
        if (others.length > 0) {
          const next = others[0];
          navigate(`/mediaDetails/${next.id}`, { replace: true });
        } else {
          // s'il n'y a vraiment plus rien, on reste dans la biblio
          navigate("/myMedia", { replace: true });
        }
      }, 700);
    } catch (e2) {
      console.error("[MediaDetail] Erreur suppression média:", e2);
      showToast("error", e2.message || t("err_delete_media"));
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // --- Action: recadrage vidéo (trim) ---
  const handleTrimVideo = async (e) => {
    e.preventDefault();
    if (!media) return;
    if (!token) {
      showToast("info", t("toast_login_edit"));
      return;
    }
    if (!canEditPersonalMedia) {
      showToast("error", t("toast_owner_only"));
      return;
    }
    const isVid = isVideoFile(media.file_path || "");
    if (!isVid) {
      showToast(
        "info",
        "Cette action n'est disponible que pour les vidéos."
      );
      return;
    }
    if (trimDuration == null) {
      showToast(
        "error",
        "Durée de la vidéo inconnue, réessayez après le chargement."
      );
      return;
    }
    if (trimStart < 0 || trimEnd <= trimStart || trimEnd > trimDuration + 0.1) {
      showToast("error", t("toast_trim_invalid"));
      return;
    }

    setSavingTrim(true);
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const payload = {
        start: trimStart,
        end: trimEnd,
      };
      const r = await fetch(`/media/${media.id}/trim`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `Erreur ${r.status}`);
      }
      let updated = null;
      try {
        updated = await r.json();
      } catch {
        // si le backend ne renvoie rien, on garde l'ancien fichier
      }

      if (updated && (updated.file_path || updated.new_file_path)) {
        const newFp = updated.file_path || updated.new_file_path;
        setMedia((prev) => ({
          ...(prev || {}),
          file_path: newFp,
          duration: updated.duration || prev?.duration,
        }));
        showToast("success", t("toast_trim_ok"));
      } else {
        showToast(
          "info",
          "Demande de recadrage envoyée. La nouvelle vidéo apparaîtra quand le traitement sera terminé."
        );
      }
      closePanel();
    } catch (e2) {
      console.error("[MediaDetail] Erreur trim vidéo:", e2);
      showToast(
        "error",
        e2.message || "Erreur lors du recadrage de la vidéo."
      );
    } finally {
      setSavingTrim(false);
    }
  };


  const mediaPath = media?.file_path || "";
  const isImage = isImageFile(mediaPath);
  const isVideo = isVideoFile(mediaPath);

  // Auto-play the main video only while it is "in focus"
  useVideoAutoPlayOnFocus(videoRef, isVideo, mediaPath);


  if (loading) {
    return (
      <div className="p-4 d-flex align-items-center gap-2">
        <div
          className="spinner-border"
          role="status"
          aria-label={t("common_loading")}
        ></div>
        <span>{t("common_loading")}</span>
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4">
        <div
          className="alert alert-danger d-flex align-items-center gap-2 m-0"
          role="alert"
        >
          <i className="bi bi-x-octagon-fill"></i>
          <span>{err}</span>
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="p-4">
        <div
          className="alert alert-warning d-flex align-items-center gap-2 m-0"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>{t("md_not_found")}</span>
        </div>
      </div>
    );
  }
  const toastIcon =
    toast?.type === "success"
      ? "bi-check-circle-fill"
      : toast?.type === "error"
        ? "bi-exclamation-octagon-fill"
        : "bi-info-circle-fill";

  const isMetaMode = panelMode === "meta" || panelMode === null;
  const isTrimMode = panelMode === "trim";

  return (
    <div className="md-wrap">
      <style>{legacyStyles}</style>

      {toast && (
        <div className={`md-toast md-toast-${toast.type || "info"}`}>
          <i className={`bi ${toastIcon}`} />
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            aria-label={t("toast_close")}
          >
            ×
          </button>
        </div>
      )}

      {/* Row: MAIN (left fixed width) | RIGHT (fills remaining) */}
      <section className="md-row">
        {/* MAIN LEFT */}
        <main className="main-col" ref={mainCardRef}>
          <article className="md-card">
            {/* actions bar ABOVE the media frame */}
            {/* media frame */}
            <div className="md-visual">
              {isImage && (
                <img
                  className="md-main"
                  src={`/uploads/${media.file_path}`}
                  alt={media.title || "media"}
                  onLoad={() => tryFillRef.current && tryFillRef.current()}
                />
              )}

              {isVideo && (
                <video
                  ref={videoRef}
                  className="md-main"
                  src={`/uploads/${media.file_path}`}
                  controls
                  playsInline
                  onLoadedMetadata={(ev) => {
                    const dur = ev.target?.duration || ev.currentTarget?.duration || 0;
                    setTrimDuration(dur || 0);
                    setTrimStart(0);
                    setTrimEnd(Math.round(dur || 0));
                    if (tryFillRef.current) tryFillRef.current();
                  }}
                />
              )}
            </div>



            {/* ✅ toolbar UNDER the media (same position as final video) */}
            <div className="md-toolbar">
              <div className="md-toolbarLeft">
                {(isImage || isVideo) && media?.user && (
                  <span className="md-ownerSlot" aria-label={t("common_owner")} title={media.user}>
                    {(() => {
                      const u = String(media.user || "").trim();
                      const src = legacyAvatarSrcForUsername(u);
                      const to = legacyResolveUserProfilePath(u);

                      const img = (
                        <img
                          className="md-ownerAvatar"
                          src={src}
                          alt={u || t("common_owner")}
                          title={u || t("common_owner")}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = legacyMakeUiAvatar(u || "?");
                          }}
                        />
                      );

                      return to ? (
                        <Link
                          to={to}
                          className="md-ownerLink"
                          aria-label={u || t("common_owner")}
                          title={u || t("common_owner")}
                        >
                          {img}
                        </Link>
                      ) : (
                        img
                      );
                    })()}
                  </span>
                )}

                {/* ✅ title right beside creator icon */}
                <h2 className="md-toolbarTitle">{media.title || "Sans titre"}</h2>
              </div>


              <div className="md-toolbarRight">
                {/* LIKE */}
                <div
                  className="md-likeWrap"
                  onMouseEnter={() => {
                    setLikesPopoverOpen(true);
                    if ((likesUsers === null || likesUsersError) && !likesUsersLoading) {
                      fetchLikesUsers();
                    }
                  }}
                  onMouseLeave={() => setLikesPopoverOpen(false)}
                >
                  <button
                    type="button"
                    className={`md-iconbtn md-like ${hasLiked ? "active" : ""}`}
                    onClick={handleToggleLike}
                    disabled={reactionsLoading}
                    title={t("common_like")}
                  >
                    <i className={hasLiked ? "bi bi-heart-fill" : "bi bi-heart"}></i>
                    <span className="md-iconcount">{likesCount || 0}</span>
                  </button>

                  {likesPopoverOpen && (
                    <div className="md-likePopover" role="dialog" aria-label={t("common_liked_by")}>
                      {likesUsersLoading ? (
                        <div className="md-likeLoading">
                          <div className="spinner-border" role="status" aria-label={t("common_loading")}></div>
                          <span>{t("common_loading_ellipsis")}</span>
                        </div>
                      ) : likesUsersError ? (
                        <div className="md-likeError">
                          <div className="md-likeErrorText">{t("err_load_likes")}</div>
                          <button
                            type="button"
                            className="md-likeRetry"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              fetchLikesUsers();
                            }}
                          >
                            {t("common_retry")}
                          </button>
                        </div>
                      ) : Array.isArray(likesUsers) && likesUsers.length > 0 ? (
                        <ul className="md-likeUsersList">
                          {likesUsers.slice(0, 24).map((u, idx) => {
                            const username = String(u?.username || "").trim();
                            const label = String(u?.displayName || username || "?");
                            const apiAvatar =
                              u?.avatarUrl ||
                              u?.avatar_url ||
                              u?.raw?.avatarUrl ||
                              u?.raw?.avatar_url ||
                              u?.raw?.profilePhoto ||
                              u?.raw?.profile_photo ||
                              u?.raw?.photo ||
                              u?.raw?.photo_url ||
                              u?.raw?.picture ||
                              null;

                            const src =
                              legacyNormalizeUploadPath(apiAvatar) ||
                              legacyAvatarSrcForUsername(username);
                            const to = legacyResolveUserProfilePath(username);

                            const avatarImg = (
                              <img
                                src={src}
                                alt={label}
                                title={label}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = legacyMakeUiAvatar(label || "?");
                                }}
                              />
                            );

                            return (
                              <li key={`${username}-${idx}`} className="md-likeUser">
                                {to ? (
                                  <Link to={to} className="md-likeUserLink" title={label}>
                                    <span className="md-likeAvatar">{avatarImg}</span>
                                    <span className="md-likeName">{label}</span>
                                  </Link>
                                ) : (
                                  <>
                                    <span className="md-likeAvatar">{avatarImg}</span>
                                    <span className="md-likeName">{label}</span>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="md-likeEmpty">{t("common_no_likes_yet")}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* COMMENTS */}
                <button
                  type="button"
                  className="md-iconbtn"
                  onClick={() => {
                    // ✅ close inline editors (no regression)
                    setEditInlineOpen(false);
                    setTrimInlineOpen(false);

                    setCommentsExpanded(true);
                    handleScrollToComments();

                    // optional: clear hash
                    try {
                      if (window.location.hash) {
                        window.history.replaceState(null, "", window.location.pathname);
                      }
                    } catch { }
                  }}
                  title={t("common_comments")}
                >
                  <i className="bi bi-chat"></i>
                  <span className="md-iconcount">{comments.length || 0}</span>
                </button>

                {/* SHARE */}
                <button
                  type="button"
                  className="md-iconbtn"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => showToast("success", t("toast_link_copied")))
                      .catch(() => showToast("error", t("toast_copy_failed")));
                  }}
                  title={t("common_share")}
                >
                  <i className="bi bi-share"></i>
                </button>

                {/* MENU three dots (owner only) */}
                {canEditPersonalMedia && (
                  <div className="md-topbar-more" ref={actionsMenuRef}>
                    <button
                      type="button"
                      className="md-iconbtn"
                      onClick={() => setShowActionsMenu((prev) => !prev)}
                      title={t("common_actions")}
                    >
                      <i className="bi bi-three-dots"></i>
                    </button>

                    {showActionsMenu && (
                      <div className="md-actions-menu">
                        <button
                          type="button"
                          className="md-actions-item"
                          onClick={() => {
                            openInlineEdit();
                            setShowActionsMenu(false);
                          }}
                        >
                          <i className="bi bi-pencil-square me-2"></i>
                          Modifier les infos
                        </button>

                        {isVideo && (
                          <button
                            type="button"
                            className="md-actions-item"
                            onClick={() => {
                              openInlineTrim();
                              setShowActionsMenu(false);
                            }}
                          >
                            <i className="bi bi-scissors me-2"></i>
                            Raccourcir la vidéo
                          </button>
                        )}

                        <button
                          type="button"
                          className="md-actions-item text-danger"
                          onClick={() => {
                            setShowDeleteConfirm(true);
                            setShowActionsMenu(false);
                          }}
                        >
                          <i className="bi bi-trash3 me-2"></i>
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <header className="md-meta">
              {media.description && (
                <p className="md-desc">{media.description}</p>
              )}
            </header>


            {/* Commentaires en bas, à la façon Pinterest */}
            <section className="md-comments" ref={commentsRef}>
              <div className="md-comments-header">
                <button
                  type="button"
                  className="md-comments-toggle"
                  onClick={() => setCommentsExpanded((v) => !v)}
                >
                  <span className="md-comments-title">
                    {comments.length} commentaire{comments.length > 1 ? "s" : ""}
                  </span>
                  <i
                    className={`bi ${commentsExpanded ? "bi-chevron-up" : "bi-chevron-down"
                      }`}
                  ></i>
                </button>
              </div>

              {commentsExpanded && comments.length > 0 && (
                <ul className="md-comments-list">
                  {comments.map((c) => {
                    const rawAuthor =
                      c?.author ||
                      c?.user ||
                      c?.username ||
                      c?.created_by ||
                      c?.createdBy ||
                      c?.owner ||
                      c?.uploader ||
                      "";
                    const author = String(rawAuthor || "").trim() || "?";
                    const src = legacyAvatarSrcForUsername(author);
                    const to = legacyResolveUserProfilePath(author);
                    const createdAt = c?.createdAt || c?.created_at;

                    const avatarImg = (
                      <img
                        src={src}
                        alt={author}
                        title={author}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = legacyMakeUiAvatar(author || "?");
                        }}
                      />
                    );

                    return (
                      <li key={c.id} className="md-comment">
                        <div className="md-comment-avatar md-comment-avatar--img">
                          {to ? (
                            <Link
                              to={to}
                              className="md-ownerLink"
                              aria-label={author}
                              title={author}
                            >
                              {avatarImg}
                            </Link>
                          ) : (
                            avatarImg
                          )}
                        </div>

                        <div className="md-comment-body">
                          <div className="md-comment-meta">
                            {to ? (
                              <Link
                                to={to}
                                className="md-comment-author md-comment-authorLink"
                              >
                                {author}
                              </Link>
                            ) : (
                              <span className="md-comment-author">{author}</span>
                            )}

                            {createdAt && (
                              <span className="md-comment-date">
                                {new Date(createdAt).toLocaleString(locale, {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            )}
                          </div>

                          <p>{c.text}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {commentsExpanded && (
                <form className="md-comment-form" onSubmit={handleAddComment}>
                  {(isImage || isVideo) ? (
                    <div
                      className="md-comment-avatar md-comment-avatar--img"
                      aria-label={t("common_owner")}
                      title={currentUsername || currentUserId || ""}
                    >
                      {(() => {
                        const u = String(currentUsername || currentUserId || "").trim();
                        const src = legacyAvatarSrcForUsername(u);
                        const to = legacyResolveUserProfilePath(u);

                        const img = (
                          <img
                            className="md-ownerAvatar"
                            src={src}
                            alt={u || t("common_owner")}
                            title={u || t("common_owner")}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = legacyMakeUiAvatar(u || "?");
                            }}
                          />
                        );

                        return to ? (
                          <Link
                            to={to}
                            className="md-ownerLink"
                            aria-label={u || t("common_owner")}
                            title={u || t("common_owner")}
                          >
                            {img}
                          </Link>
                        ) : (
                          img
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="md-comment-avatar">
                      {String(currentUsername || currentUserId || "M").charAt(0).toUpperCase()}
                    </div>
                  )}

                  <div className="md-comment-input-wrap">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={t("common_add_comment_ph")}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="btn btn-sm btn-dark"
                      disabled={!newComment.trim()}
                    >
                      Publier
                    </button>
                  </div>
                </form>
              )}


            </section>

            {/* ✅ Éditer les infos (inline) — sous les commentaires */}
            {/* ✅ Panneau inline unique (onglets) sous les commentaires */}
            {canEditPersonalMedia && (editInlineOpen || trimInlineOpen) && (
              <section className="md-inline-panel" id={trimInlineOpen ? "trim" : "edit"}>
                {/* Onglets */}
                <div className="md-inline-tabs">
                  <button
                    type="button"
                    className={`md-inline-tab ${!trimInlineOpen ? "active" : ""}`}
                    onClick={() => {
                      setEditInlineOpen(true);
                      setTrimInlineOpen(false);
                      try {
                        window.history.replaceState(null, "", `${window.location.pathname}#edit`);
                      } catch { }
                    }}
                  >
                    <i className="bi bi-pencil-square"></i>
                    <span>{t("md_edit_info_alt")}</span>
                  </button>

                  {isVideo && (
                    <button
                      type="button"
                      className={`md-inline-tab ${trimInlineOpen ? "active" : ""}`}
                      onClick={() => {
                        setTrimInlineOpen(true);
                        setEditInlineOpen(false);
                        try {
                          window.history.replaceState(null, "", `${window.location.pathname}#trim`);
                        } catch { }
                      }}
                    >
                      <i className="bi bi-scissors"></i>
                      <span>{t("md_trim_video")}</span>
                    </button>
                  )}
                </div>

                {/* Contenu */}
                <div className="md-inline-body">
                  {/* TAB: META */}
                  {!trimInlineOpen && (
                    <form className="md-edit-form" onSubmit={handleSaveMeta}>
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">{t("common_title")}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder={t("md_title_ph")}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold">{t("common_description")}</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={3}
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder={t("md_desc_ph")}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label small fw-semibold">{t("common_location_context")}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editLocation}
                          onChange={(e) => setEditLocation(e.target.value)}
                          placeholder={t("md_location_ph")}
                        />
                      </div>

                      <div className="d-flex justify-content-end gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() => setEditInlineOpen(false)}
                          disabled={savingMeta}
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          className="btn btn-sm btn-dark"
                          disabled={savingMeta}
                        >
                          {savingMeta ? "Enregistrement…" : "Enregistrer"}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* TAB: TRIM */}
                  {trimInlineOpen && isVideo && (
                    <form className="md-trim-form" onSubmit={handleTrimVideo}>
                      <div className="mb-2 d-flex justify-content-between small text-muted">
                        <span>
                          Durée totale :{" "}
                          {trimDuration != null ? `${trimDuration.toFixed(1)} s` : "…"}
                        </span>
                      </div>

                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <label className="form-label small">{t("md_start_seconds")}</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min={0}
                            max={trimEnd || undefined}
                            step="0.1"
                            value={trimStart}
                            onChange={(e) => setTrimStart(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label small">{t("md_end_seconds")}</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            min={trimStart || 0}
                            max={trimDuration || undefined}
                            step="0.1"
                            value={trimEnd}
                            onChange={(e) => setTrimEnd(parseFloat(e.target.value) || 0)}
                          />
                        </div>
                      </div>

                      <p className="small text-muted mb-2">
                        La vidéo générée contiendra uniquement la portion comprise entre{" "}
                        <strong>début</strong> et <strong>fin</strong>.
                      </p>

                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={() => setTrimInlineOpen(false)}
                          disabled={savingTrim}
                        >
                          Annuler
                        </button>
                        <button
                          type="submit"
                          className="btn btn-sm btn-dark"
                          disabled={savingTrim}
                        >
                          {savingTrim ? "Recadrage…" : "Appliquer le recadrage"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </section>
            )}


          </article>
        </main>

        {/* RIGHT — palette d’édition rapide + suggestions Pinterest */}
        <aside className="right-rail" ref={rightRailRef}>
          {canEditPersonalMedia && (panelMode !== null || showDeleteConfirm) && (
            <div className="md-side-panel">
              <div className="md-side-header">
                <div>
                  <h3 className="md-side-title">{t("md_edit_media")}</h3>
                  <p className="md-side-sub">
                    Ajustez les informations ou le découpage vidéo sans quitter
                    la page.
                  </p>
                </div>
                <button
                  type="button"
                  className="md-side-close"
                  onClick={closePanel}
                  aria-label={t("toast_close_panel")}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <div className="md-side-tabs">
                <button
                  type="button"
                  className={`md-side-tab ${isMetaMode ? "active" : ""
                    }`}
                  onClick={() => setPanelMode("meta")}
                >
                  Infos du média
                </button>
                {isVideo && (
                  <button
                    type="button"
                    className={`md-side-tab ${isTrimMode ? "active" : ""
                      }`}
                    onClick={() => setPanelMode("trim")}
                  >
                    Raccourcir la vidéo
                  </button>
                )}
              </div>

              {isMetaMode && (
                <form className="md-edit-form" onSubmit={handleSaveMeta}>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">
                      Titre
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder={t("md_title_ph")}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold">
                      Description
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder={t("md_desc_ph")}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label small fw-semibold">
                      Lieu / contexte
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder={t("md_location_ph")}
                    />
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={closePanel}
                      disabled={savingMeta}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-sm btn-dark"
                      disabled={savingMeta}
                    >
                      {savingMeta ? "Enregistrement…" : "Enregistrer"}
                    </button>
                  </div>
                </form>
              )}

              {isTrimMode && isVideo && (
                <form
                  id="md-trim-form"
                  className="md-trim-form mt-3"
                  onSubmit={handleTrimVideo}
                >
                  <div className="mb-2 d-flex justify-content-between small text-muted">
                    <span>
                      Durée totale :{" "}
                      {trimDuration != null
                        ? `${trimDuration.toFixed(1)} s`
                        : "…"}
                    </span>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label small">
                        Début (secondes)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min={0}
                        max={trimEnd || undefined}
                        step="0.1"
                        value={trimStart}
                        onChange={(e) =>
                          setTrimStart(parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small">
                        Fin (secondes)
                      </label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        min={trimStart || 0}
                        max={trimDuration || undefined}
                        step="0.1"
                        value={trimEnd}
                        onChange={(e) =>
                          setTrimEnd(parseFloat(e.target.value) || 0)
                        }
                      />
                    </div>
                  </div>

                  <p className="small text-muted mb-2">
                    La vidéo générée contiendra uniquement la portion comprise
                    entre <strong>début</strong> et <strong>fin</strong>.
                  </p>

                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={closePanel}
                      disabled={savingTrim}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-sm btn-dark"
                      disabled={savingTrim}
                    >
                      {savingTrim ? "Recadrage…" : "Appliquer le recadrage"}
                    </button>
                  </div>
                </form>
              )}

              {showDeleteConfirm ? (
                <div className="alert alert-warning d-flex flex-column gap-2 py-2 px-3 mt-3 small">
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle-fill" />
                    <span>
                      Supprimer définitivement ce média ? Cette action est
                      irréversible.
                    </span>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={handleDeleteMedia}
                      disabled={deleting}
                    >
                      {deleting ? "Suppression…" : "Confirmer la suppression"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="md-side-footer">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleting}
                  >
                    <i className="bi bi-trash3 me-1" />
                    Supprimer ce média
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Suggestions type Pinterest */}
          <div className="rail-masonry">
            {rightSim.map((m, idx) => (
              <LegacyTile
                key={`R-${m.id ?? `idx-${idx}`}`}
                item={m}
                onLoad={() => tryFillRef.current && tryFillRef.current()}
              />
            ))}
          </div>
        </aside>
      </section>

      {/* BOTTOM — overflow (auto width) */}
      <section className="md-bottom">
        <div className="bottom-masonry">
          {bottomSim.map((m, idx) => (
            <LegacyTile key={`B-${m.id ?? `idx-${idx}`}`} item={m} />
          ))}
        </div>
      </section>
    </div>
  );
}

function LegacyTile({ item, onLoad }) {
  const isVid = isVideoFile(item.file_path || "");
  const to = `/mediaDetails/${item.id}`;
  return (
    <Link to={to} className="pin">
      {isVid ? (
        <video
          className="pin-media"
          src={`/uploads/${item.file_path}`}
          muted
          playsInline
          onLoadedMetadata={onLoad}
        />
      ) : (
        <img
          className="pin-media"
          src={`/uploads/${item.file_path}`}
          alt={item.title || "media"}
          loading="lazy"
          onLoad={onLoad}
        />
      )}
    </Link>
  );
}

/* ================= CSS legacy ================= */
const legacyStyles = `
/* Container */
.md-wrap{
  width: 100%;
  max-width: none;
  margin: 0 auto;
  padding: 86px 24px 40px;
  box-sizing: border-box;
}

/* 2 columns: MAIN fixed width (left) | RIGHT auto (fills available) */
.md-row{
  display: grid;
  grid-template-columns: minmax(640px, 860px) 1fr;
  gap: 22px;
  align-items: start;
}

/* MAIN card (left) */
.md-card{
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(0,0,0,.08);
  overflow: visible;
}
.md-visual{
  position: relative;
  background: #f7f8f9;
  display:flex;
  align-items:center;
  justify-content:center;
  padding: 8px;
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  overflow: hidden;
}


.md-main{
  width: 100%;
  max-height: 72vh;
  object-fit: contain;
  display: block;
}
@media (max-width: 992px){
  .md-main{ max-height: 58vh; }
}

.md-toolbar{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:6px 16px 10px;
}

/* Toolbar left/right layout (for FINAL contributors stack) */
.md-toolbarLeft{
  display:flex;
  align-items:center;
  gap:10px;
  min-width:0;
}
.md-toolbarRight{
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:10px;
  flex-wrap:wrap;
}
.md-toolbarContrib{
  display:flex;
  align-items:center;
  gap:10px;
}
.md-toolbarContrib i{
  font-size:18px;
  line-height:1;
  color:#111827;
}
.md-avatars--toolbar{
  justify-content:flex-start;
}

.md-toolbarTitle{
  margin:0;
  min-width:0;
  font-size:1.1rem;
  font-weight:600;
  color:#111827;
  line-height:1.1;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
@media (max-width: 520px){
  .md-toolbarTitle{ font-size:1rem; }
}

/* icon button = same visual style everywhere */
.md-iconbtn{
  position:relative;
  height:40px;
  padding:0 12px;
  border:none;
  border-radius:999px;
  background:#f1f3f5;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  cursor:pointer;
  transition:.12s ease;
  box-shadow:0 2px 8px rgba(0,0,0,.06);
}

/* default icon color */
.md-iconbtn i{
  font-size:18px;
  line-height:1;
  color:#111827; /* neutral dark */
}

.md-likeTip{
  position:absolute;
  top:-34px;
  left:50%;
  transform:translate(-50%, 6px);
  background:#111827;
  color:#fff;
  font-size:0.72rem;
  padding:4px 8px;
  border-radius:999px;
  white-space:nowrap;
  opacity:0;
  pointer-events:none;
  transition:opacity .12s ease, transform .12s ease;
  box-shadow:0 8px 20px rgba(0,0,0,.18);
  z-index:5;
}
.md-iconbtn:hover .md-likeTip{
  opacity:1;
  transform:translate(-50%, 0px);
}




.md-likeWrap{
  position:relative;
  display:inline-flex;
  z-index:2000;
}

/* Likes popover (list of users who liked) */
.md-likePopover{
  position:absolute;
  top:calc(100% + 10px);
  right:0;
  min-width:260px;
  max-width:320px;
  background:#ffffff;
  border:1px solid rgba(15, 23, 42, .12);
  border-radius:16px;
  box-shadow:0 22px 60px rgba(0,0,0,.18);
  padding:10px;
  z-index:2000;
}
.md-likePopover:before{
  content:"";
  position:absolute;
  top:-6px;
  right:18px;
  width:12px;
  height:12px;
  background:#ffffff;
  border-left:1px solid rgba(15, 23, 42, .12);
  border-top:1px solid rgba(15, 23, 42, .12);
  transform:rotate(45deg);
}

.md-likePopoverHeader{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:6px 8px 10px;
  border-bottom:1px solid rgba(15, 23, 42, .08);
  margin-bottom:8px;
}
.md-likePopoverTitle{
  display:flex;
  align-items:center;
  gap:8px;
  font-weight:500;
  color:#0f172a;
}
.md-likePopoverTitle i{
  color:#e11d48;
  font-size:16px;
}
.md-likePopoverCount{
  font-weight:500;
  color:#111827;
  font-size:.95rem;
}

.md-likeLoading{
  display:flex;
  align-items:center;
  gap:10px;
  padding:10px 8px;
  color:#475569;
  font-weight:500;
}
.md-likeLoading .spinner-border{
  width:1.05rem;
  height:1.05rem;
}

.md-likeError{
  padding:10px 8px;
  color:#b91c1c;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.md-likeErrorText{
  flex:1 1 auto;
  font-weight:600;
  color:#b91c1c;
  font-size:.86rem;
}
.md-likeRetry{
  flex:0 0 auto;
  border:1px solid rgba(185, 28, 28, .25);
  background:rgba(185, 28, 28, .06);
  color:#b91c1c;
  font-weight:800;
  padding:6px 10px;
  border-radius:999px;
  cursor:pointer;
}
.md-likeRetry:hover{
  background:rgba(185, 28, 28, .10);
}
.md-likeEmpty{
  padding:10px 8px;
  color:#475569;
  font-weight:500;
}

.md-likeUsersList{
  list-style:none;
  padding:0;
  margin:0;
  max-height:240px;
  overflow:auto;
}

.md-likeUser{
  display:flex;
  align-items:center;
  gap:10px;
  padding:6px;
  border-radius:14px;
}
.md-likeUserLink{
  display:flex;
  align-items:center;
  gap:10px;
  width:100%;
  text-decoration:none;
  color:inherit;
  padding:6px;
  border-radius:14px;
}
.md-likeUserLink:hover{
  background:rgba(15, 23, 42, .04);
}
.md-likeAvatar{
  width:30px;
  height:30px;
  border-radius:999px;
  overflow:hidden;
  flex:0 0 30px;
  background:#e2e8f0;
  border:1px solid rgba(15, 23, 42, .10);
}
.md-likeAvatar img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
}
.md-likeName{
  font-weight:400;
  color:#0f172a;
  font-size:.92rem;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

/* LIKE active = RED */
.md-iconbtn.md-like.active i,
.md-iconbtn.active i.bi-heart-fill{
  color:#e11d48; /* same red as existing app */
}


.md-iconcount{
  font-size:.9rem;
  font-weight:600;
  color:currentColor; 
}

.md-comments-toggle i{
  color:#111827;
  font-size:0.9rem;
}
/* ✅ comments header “commentaire(s)” unified */
.md-comments-toggle{
  border:none;
  background:transparent;
  padding:0;
  display:flex;
  align-items:center;
  gap:8px;
  cursor:pointer;
}

.md-comments-title{
  font-size:1rem;
  font-weight:500;        /* not bold */
  color:#111827;
  letter-spacing:0;
}

.md-iconbtn:hover{ background:#e9ecef; transform: translateY(-1px); }

.md-meta{ padding: 8px 16px 16px; }
.md-title{ margin:6px 0 4px; font-size:1.25rem; font-weight:600; }
.md-desc{ margin:0 0 8px; color:#495057; }
.md-line{ display:flex; flex-wrap:wrap; gap:12px; color:#6c757d; font-size:.95rem; }
.md-line i{ margin-right:6px; }

/* Creator avatar (FINAL) */
.md-creatorSlot{ display:inline-flex; align-items:center; }
.md-creatorLink{ display:inline-flex; align-items:center; justify-content:center; text-decoration:none; }
.md-creatorAvatar{
  width:34px;
  height:34px;
  border-radius:999px;
  object-fit:cover;
  background:#e5e7eb;
  border:2px solid #fff;
  box-shadow:none;
}
@media (max-width: 520px){
  .md-creatorAvatar{ width:30px; height:30px; }
}





/* Owner avatar (LEGACY media) */
.md-ownerSlot{ display:inline-flex; align-items:center; }
.md-ownerLink{ display:inline-flex; align-items:center; justify-content:center; text-decoration:none; }
.md-ownerAvatar{
  width:30px;
  height:30px;
  border-radius:999px;
  object-fit:cover;
  background:#e5e7eb;
  border:2px solid #fff;
  box-shadow:none;
}
@media (max-width: 520px){
  .md-ownerAvatar{ width:26px; height:26px; }
}

/* Comment input avatar as image (owner only) */
.md-comment-avatar--img{ padding:0; overflow:hidden; }
.md-comment-avatar--img img{ width:100%; height:100%; border-radius:999px; object-fit:cover; display:block; }
/* Contributors avatars stack (final project) */
.md-contribRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin: 10px 0 6px;
}
.md-contribLabel{
  display:flex;
  align-items:center;
  gap:8px;
  font-size:0.86rem;
  color:#6b7280;
}
.md-avatars{
  display:flex;
  align-items:center;
  justify-content:flex-end;
}
.md-avatarLink{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  text-decoration:none;
}
.md-avatar{
  width:34px;
  height:34px;
  border-radius:999px;
  object-fit:cover;
  background:#e5e7eb;
  border:2px solid #fff;
  box-shadow:none;
  margin-left:-10px;
}
.md-avatarLink:first-child .md-avatar{
  margin-left:0;
}
.md-avatarMore{
  width:34px;
  height:34px;
  border-radius:999px;
  border:2px solid #fff;
  background:#111827;
  color:#fff;
  font-size:0.85rem;
  font-weight:700;
  display:flex;
  align-items:center;
  justify-content:center;
  margin-left:-10px;
  box-shadow:none;
}
@media (max-width: 520px){
  .md-avatar, .md-avatarMore{ width:30px; height:30px; }
}


/* RIGHT masonry fills remaining width */
.right-rail{ width: 100%; }
.rail-masonry{
  column-count: 3;
  column-gap: 16px;
}
@media (max-width: 1400px){ .rail-masonry{ column-count:3; } }
@media (max-width: 1200px){ .rail-masonry{ column-count:2; } }
@media (max-width: 1024px){ .rail-masonry{ column-count:2; } }

/* Bottom full-width masonry (overflow) */
.md-bottom{ margin-top: 24px; }
.bottom-masonry{
  column-count: 6;
  column-gap: 16px;
}
@media (max-width: 1400px){ .bottom-masonry{ column-count:5; } }
@media (max-width: 1200px){ .bottom-masonry{ column-count:4; } }
@media (max-width: 992px){  .bottom-masonry{ column-count:3; } }

/* Tiles */
.pin{
  display:block;
  background:#fff;
  border-radius:12px;
  overflow:hidden;
  box-shadow: 0 4px 14px rgba(0,0,0,.07);
  margin: 0 0 16px;
  break-inside: avoid;
  transition: transform .12s ease, box-shadow .12s ease;
}
.pin:hover{ transform: translateY(-2px); box-shadow: 0 8px 18px rgba(0,0,0,.10); }
.pin-media{ width:100%; height:auto; display:block; object-fit:cover; }

/* Edit / manage panel (ancienne version, gardée pour compatibilité éventuelle) */
.md-edit-panel{
  margin: 8px 16px 16px;
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
}
.md-edit-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  margin-bottom:8px;
}
.md-edit-title{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:0.9rem;
  font-weight:600;
  color:#334155;
}
.md-edit-form{
  margin-top:4px;
}
.md-edit-subtitle{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:0.85rem;
  font-weight:500;
  color:#475569;
}
.md-trim-form small{
  font-size:0.78rem;
}

/* Toast bottom-right */
.md-toast{
  position: fixed;
  right: 16px;
  bottom: 16px;
  padding: 10px 14px;
  border-radius: 12px;
  box-shadow:
    0 0 0 1px rgba(15,23,42,.06),
    0 10px 30px rgba(15,23,42,.35);
  font-size: 0.85rem;
  display:flex;
  align-items:center;
  gap:8px;
  color:#fff;
  z-index: 2000;
}
.md-toast-info{ background:#0f172a; }
.md-toast-success{ background:#16a34a; }
.md-toast-error{ background:#dc2626; }
.md-toast button{
  border:none;
  background:transparent;
  color:inherit;
  font-size:1.1rem;
  line-height:1;
  cursor:pointer;
}

/* Mobile: stack */
@media (max-width: 900px){
  .md-row{ grid-template-columns: 1fr; }
  .rail-masonry{ column-count: 2; }
  .bottom-masonry{ column-count: 2; }
}
@media (max-width: 520px){
  .rail-masonry{ column-count: 1; }
  .bottom-masonry{ column-count: 1; }
  .md-wrap{ padding-left: 12px; padding-right: 12px; }
}
/* ✅ Inline panel (tabs) under comments */
.md-inline-panel{
  margin: 12px 16px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(15,23,42,.08);
  box-shadow: 0 8px 22px rgba(15,23,42,.08);
  overflow: visible;
}

.md-inline-tabs{
  display:flex;
  gap:8px;
  padding: 10px 10px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(15,23,42,.08);
}

.md-inline-tab{
  border: none;
  background: transparent;
  border-radius: 999px;
  padding: 8px 12px;
  display:flex;
  align-items:center;
  gap:8px;
  font-weight: 700;
  font-size: 0.9rem;
  color:#334155;
  cursor:pointer;
  transition: .12s ease;
}

.md-inline-tab:hover{
  background: rgba(15,23,42,.06);
}

.md-inline-tab.active{
  background: #0f172a;
  color: #fff;
}

.md-inline-body{
  padding: 14px 14px 12px; /* ✅ évite l'effet "attaché" */
}



/* Pinterest-style actions (NOT over the media anymore) */
.md-topbar{
  position: static;
  display:flex;
  align-items:center;
  justify-content:flex-end;
  gap:8px;
  z-index:auto;
}

.md-topbtn{
  width:34px;
  height:34px;
  border:none;
  border-radius:999px;
  background:rgba(255,255,255,0.92);
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 4px 12px rgba(15,23,42,.18);
  cursor:pointer;
  transition:.14s ease;
}
.md-topbtn:hover{
  background:#f3f4f6;
  transform:translateY(-1px);
}
.md-topbar-more{
  position:relative;
}
.md-actions-menu{
  position:absolute;
  top:115%;
  right:0;
  min-width:190px;
  background:#fff;
  border-radius:12px;
  box-shadow:0 18px 40px rgba(15,23,42,.25);
  padding:6px 0;
  z-index:10;
}
.md-actions-item{
  width:100%;
  padding:8px 14px;
  border:none;
  background:transparent;
  text-align:left;
  font-size:0.87rem;
  display:flex;
  align-items:center;
  cursor:pointer;
}
.md-actions-item:hover{
  background:#f3f4f6;
}
.md-actions-item.text-danger{
  color:#b91c1c;
}
.md-actions-item.text-danger:hover{
  background:#fee2e2;
}
.md-actions-item i{
  font-size:1rem;
}

/* Comments block */
.md-comments{
  border-top:1px solid #e5e7eb;
  margin:4px 0 0;
  padding:12px 16px 16px;
}
.md-comments-header{
  display:flex;
  align-items:baseline;
  justify-content:space-between;
  gap:8px;
  margin-bottom:8px;
}
.md-comments-header h3{
  font-size:0.95rem;
  font-weight:600;
  margin:0;
}
.md-comments-count{
  font-size:0.8rem;
  color:#6b7280;
}
.md-comments-list{
  list-style:none;
  padding:0;
  margin:0 0 8px;
  display:flex;
  flex-direction:column;
  gap:8px;
}
.md-comment{
  display:flex;
  gap:8px;
}
.md-comment-avatar{
  width:32px;
  height:32px;
  border-radius:999px;
  background:#e5e7eb;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:0.85rem;
  font-weight:600;
  color:#374151;
  flex-shrink:0;
}
.md-comment-body{
  background:#f9fafb;
  border-radius:12px;
  padding:6px 10px;
  flex:1;
}
.md-comment-meta{
  display:flex;
  align-items:center;
  gap:6px;
  font-size:0.78rem;
  color:#6b7280;
}
.md-comment-author{
  font-weight:600;
  color:#374151;
}
.md-comment-authorLink{
  text-decoration:none;
}
.md-comment-authorLink:hover{
  text-decoration:underline;
}
.md-comment-body p{
  margin:2px 0 0;
  font-size:0.85rem;
}
.md-comment-form{
  display:flex;
  align-items:center;
  gap:8px;
  margin-top:4px;
}
.md-comment-input-wrap{
  display:flex;
  gap:6px;
  flex:1;
}
.md-comment-input-wrap input{
  flex:1;
}

@media (max-width: 520px){
  .md-comments{
    padding-left:12px;
    padding-right:12px;
  }
}

/* Palette latérale de modification rapide */
.md-side-panel{
  background:#fff;
  border-radius:18px;
  box-shadow:0 10px 28px rgba(0,0,0,.08);
  padding:14px 16px 12px;
  margin-bottom:16px;
}
.md-side-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:8px;
  margin-bottom:10px;
}
.md-side-title{
  font-size:0.98rem;
  font-weight:600;
  margin:0;
}
.md-side-sub{
  font-size:0.8rem;
  color:#6b7280;
  margin:2px 0 0;
}
.md-side-close{
  border:none;
  background:transparent;
  padding:4px;
  border-radius:999px;
  display:flex;
  align-items:center;
  justify-content:center;
  cursor:pointer;
}
.md-side-close:hover{
  background:#f3f4f6;
}
.md-side-tabs{
  display:flex;
  gap:6px;
  margin-bottom:10px;
}
.md-side-tab{
  flex:1;
  border-radius:999px;
  border:1px solid #e5e7eb;
  background:#f9fafb;
  padding:4px 10px;
  font-size:0.8rem;
  font-weight:500;
  cursor:pointer;
}
.md-side-tab.active{
  background:#111827;
  border-color:#111827;
  color:#fff;
}
.md-side-footer{
  margin-top:10px;
  display:flex;
  justify-content:flex-start;
}

/* ======================================================================= */

`;

/* ============================================================================
 * 3) LAYOUT VIDÉO FINALE — PROJET + MÉDIAS UTILISÉS
 * ========================================================================== */

function FinalVideoDetails({ t = (k) => k, lang = "en", locale = "en-US" }) {
  const { token } = useAuth();
  const { id } = useParams(); // ex : "final-77"

  // -------------------------
  // STATES
  // -------------------------
  const [media, setMedia] = useState(null);
  const [usedMedia, setUsedMedia] = useState([]);
  const [allPool, setAllPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usedLoading, setUsedLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Likes
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [reactionsLoading, setReactionsLoading] = useState(false);

  // Likes popover (who liked)
  const [likesPopoverOpen, setLikesPopoverOpen] = useState(false);
  const [likesUsers, setLikesUsers] = useState(null); // null = not loaded yet
  const [likesUsersLoading, setLikesUsersLoading] = useState(false);
  const [likesUsersError, setLikesUsersError] = useState(null);

  // Comments
  const [comments, setComments] = useState([]);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const finalVideoRef = useRef(null);


  const showToast = (type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);


  // -------------------------
  // EXTRACT PROJECT ID
  // -------------------------
  const projectId = useMemo(() => {
    const raw = String(id || ""); // ex 'final-77'
    if (!raw.startsWith("final-")) return null;
    const seg = raw.split("-")[1];
    const pid = Number(seg);
    return Number.isFinite(pid) ? pid : null;
  }, [id]);


  // -------------------------
  // USERS AVATARS (real) + DEMO CONTRIBUTORS (fake)
  // (UI only — does not change business logic)
  // -------------------------
  const [usersByUsername, setUsersByUsername] = useState({});
  const [avatarBust, setAvatarBust] = useState(0);

  useEffect(() => {
    if (!token) return;

    fetch("/users", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) return [];
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return [];
        return r.json();
      })
      .then((arr) => {
        const map = {};
        (arr || []).forEach((u) => {
          if (u?.username) map[u.username] = u;
        });
        setUsersByUsername(map);
        setAvatarBust(Date.now());
      })
      .catch(() => setUsersByUsername({}));
  }, [token]);

  const normalizeUploadPath = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    const cleaned = raw.replace(/^\/?uploads\/?/i, "");
    return `/uploads/${cleaned}`;
  };

  const toInitials = (nameOrUsername) => {
    const raw = String(nameOrUsername || "").trim();
    if (!raw) return "?";
    const base = raw.includes("@") ? raw.split("@")[0] : raw;
    const parts = base.split(/[^a-zA-Z0-9]+/g).filter(Boolean);
    const first = parts[0]?.charAt(0) || base.charAt(0) || "?";
    const second = parts[1]?.charAt(0) || "";
    return (first + second).toUpperCase();
  };

  const makeUiAvatar = (label) => {
    const initials = toInitials(label);
    // simple, reliable fallback avatar
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=0F172A&color=ffffff&rounded=true&size=80`;
  };

  const appendBust = (src) => {
    if (!src) return src;
    const sep = src.includes("?") ? "&" : "?";
    return `${src}${sep}v=${avatarBust}`;
  };

  const avatarSrcForUsername = (username) => {
    const u = String(username || "").trim();
    if (!u) return makeUiAvatar("?");
    const rec = usersByUsername?.[u];

    const cand =
      rec?.profilePhoto ||
      rec?.profile_photo ||
      rec?.photo ||
      rec?.photoUrl ||
      rec?.photo_url ||
      rec?.avatar ||
      rec?.avatarUrl ||
      rec?.avatar_url ||
      rec?.picture ||
      rec?.image ||
      rec?.imageUrl ||
      rec?.image_url ||
      null;

    const resolved = normalizeUploadPath(cand) || makeUiAvatar(u);
    return appendBust(resolved);
  };

  const resolveUserProfilePath = (username) => {
    const u = String(username || "").trim();
    if (!u) return null;
    return `/users/${encodeURIComponent(u)}`;
  };

  const extractContributorUsername = (m) =>
    m?.created_by ||
    m?.createdBy ||
    m?.owner ||
    m?.user ||
    m?.username ||
    m?.uploader ||
    m?.uploader_username ||
    m?.contributor ||
    m?.added_by ||
    m?.addedBy ||
    m?.author ||
    "";

  // real contributors from DB (project owner + used media creators)
  const realContributorUsernames = useMemo(() => {
    const set = new Set();
    const out = [];

    const push = (val) => {
      const u = String(val || "").trim();
      if (!u) return;
      if (set.has(u)) return;
      set.add(u);
      out.push(u);
    };

    push(media?.user);
    (usedMedia || []).forEach((m) => push(extractContributorUsername(m)));

    return out;
  }, [media?.user, usedMedia]);

  // deterministic random helpers (stable per projectId)
  const fnv1a = (str) => {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };

  const rand01 = (seedStr) => {
    let x = fnv1a(String(seedStr));
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return (x >>> 0) / 4294967296;
  };

  const fakeContributors = useMemo(() => {
    if (!projectId) return [];

    const min = 10;
    const max = 24;
    const count = Math.floor(rand01(`count-${projectId}`) * (max - min + 1)) + min;

    const arr = [];
    for (let i = 0; i < count; i++) {
      const imgId = Math.floor(rand01(`img-${projectId}-${i}`) * 70) + 1;
      const label = `Participant ${i + 1}`;
      arr.push({
        key: `fake-${projectId}-${i}`,
        label,
        avatar: `https://i.pravatar.cc/80?img=${imgId}`,
        to: null,
        fallback: label,
      });
    }

    return arr;
  }, [projectId]);

  const contributorsUi = useMemo(() => {
    const real = (realContributorUsernames || []).map((u) => ({
      key: `real-${u}`,
      label: u.includes("@") ? u.split("@")[0] : u,
      avatar: avatarSrcForUsername(u),
      to: resolveUserProfilePath(u),
      fallback: u,
    }));

    // UI-only: enrich with demo contributors (fake)
    return [...real, ...(fakeContributors || [])];
  }, [realContributorUsernames, usersByUsername, avatarBust, fakeContributors]);

  // -------------------------
  // 1) LOAD FINAL VIDEO METADATA
  // -------------------------
  useEffect(() => {
    if (!projectId) {
      setErr("Projet invalide");
      setLoading(false);
      return;
    }

    setLoading(true);
    setErr(null);

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    (async () => {
      try {
        const r = await fetch(`/projects/${projectId}/recap-ui`, { headers });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const txt = await r.text();
          throw new Error("Réponse invalide : " + txt.slice(0, 120));
        }
        const j = await r.json();
        if (!r.ok || j.error) throw new Error(j.error || "Erreur serveur");

        // Final video path
        const fp = (j.finalVideoUrl || j.videoUrl || "").replace(/^\/?output\/?/, "");

        const data = {
          id: `final-${projectId}`, // used for like/comments route
          title: j.title || "Vidéo finale du projet",
          description: j.description || j.scenario_description || j.script || "",
          file_path: fp,
          user: j.created_by || j.owner || "",
          created_at: j.updated_at || j.projectCreatedAt || j.created_at,
          visibility: "PUBLIC",
          project_id: projectId,
        };

        setMedia(data);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, token]);

  // -------------------------
  // 2) INITIALIZE LIKE STATE AFTER MEDIA LOADED
  // -------------------------
  useEffect(() => {
    if (!media) return;

    // reset popover cache for this final video
    setLikesPopoverOpen(false);
    setLikesUsers(null);
    setLikesUsersError(null);

    loadFinalReactions();
    loadComments();
  }, [media]);


  // -------------------------
  // 3) LIKE HANDLER
  // -------------------------
  const loadFinalReactions = async () => {
    try {
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const resp = await fetch(`/projects/${projectId}/final/reactions`, { headers });
      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return;

      const j = await resp.json();
      setLikesCount(j.likes_count ?? 0);
      setHasLiked(!!j.user_has_liked);
    } catch (e) {
      console.warn("Erreur final reactions:", e);
    }
  };


  const fetchFinalLikesUsers = async () => {
    if (!projectId) return;

    try {
      setLikesUsersLoading(true);
      setLikesUsersError(null);

      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const resp = await fetch(`/projects/${projectId}/final/likes`, { headers });
      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Réponse invalide du serveur.");

      const data = await resp.json();
      if (!resp.ok || data?.error) throw new Error(data?.error || "Failed to load likes");

      const arr = Array.isArray(data)
        ? data
        : (data?.users || data?.liked_by || data?.likes || []);

      setLikesUsers(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.warn("Erreur chargement likes (final):", e);
      setLikesUsersError(e?.message || "Failed to load likes");
      // keep as null so the UI shows retry state
      setLikesUsers(null);
    } finally {
      setLikesUsersLoading(false);
    }
  };

  const handleToggleLike = async () => {
    if (!token) {
      showToast("info", t("toast_login_like_video"));
      return;
    }

    try {
      setReactionsLoading(true);

      const resp = await fetch(`/projects/${projectId}/final/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Réponse invalide du serveur.");

      const data = await resp.json();
      if (!resp.ok || data.error) throw new Error(data.error);

      setLikesCount(data.likes_count ?? 0);
      setHasLiked(!!data.user_has_liked);

      // keep popover list fresh if already loaded
      if (likesUsers !== null) {
        try {
          fetchFinalLikesUsers();
        } catch { }
      }
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setReactionsLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const resp = await fetch(`/projects/${projectId}/final/comments`, { headers });
      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) return;

      const j = await resp.json();
      setComments(j.comments || []);
    } catch (e) {
      console.warn("Erreur commentaires:", e);
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    if (!token) return showToast("info", t("toast_login_comment"));

    setCommentLoading(true);

    try {
      const resp = await fetch(`/projects/${projectId}/final/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newComment }),
      });

      const ct = resp.headers.get("content-type") || "";
      if (!ct.includes("application/json")) throw new Error("Réponse invalide du serveur.");

      const j = await resp.json();
      if (!resp.ok) throw new Error(j.error || "Erreur");

      setNewComment("");
      await loadComments();
      await loadFinalReactions();
      showToast("success", t("toast_comment_added"));
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setCommentLoading(false);
    }
  };



  // -------------------------
  // 5) LOAD USED MEDIA + POOL
  // -------------------------
  useEffect(() => {
    if (!projectId) return;

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    setUsedLoading(true);

    const extractArray = (j) => {
      if (Array.isArray(j)) return j;
      if (Array.isArray(j?.media)) return j.media;
      if (Array.isArray(j?.medias)) return j.medias;
      if (Array.isArray(j?.items)) return j.items;
      return [];
    };

    const tryRoutes = async () => {
      const urls = [
        `/projects/${projectId}/media-used`,
        `/projects/${projectId}/media`,
      ];

      for (const url of urls) {
        try {
          const r = await fetch(url, { headers });
          if (!r.ok) continue;
          const j = await r.json();
          const arr = extractArray(j);
          if (arr.length) return arr;
        } catch (e) { }
      }
      return [];
    };

    (async () => {
      const items = await tryRoutes();
      setUsedMedia(items);
      setUsedLoading(false);
    })();
  }, [projectId, token]);

  useEffect(() => {
    (async () => {
      const all = await fetchLightMediaPool(token);
      all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllPool(all);
    })();
  }, [token]);

  const candidates = useMemo(() => {
    const merged = [
      ...(usedMedia || []).map((m) => ({ ...m, sourceKind: m?.sourceKind || "PROJECT_MEDIA" })),
      ...(allPool || []).map((m) => ({ ...m, sourceKind: m?.sourceKind || "MEDIA" })),
    ];
    const seen = new Set();
    const out = [];
    for (const m of merged) {
      const src = String(m?.sourceKind || m?.source || "MEDIA").toUpperCase();
      const key = `${src}:${m?.id ?? m?.file_path ?? ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push(m);
      }
    }
    return out;
  }, [usedMedia, allPool]);

  const sideList = candidates.slice(0, 20);
  const bottomList = candidates.slice(20);

  // -------------------------
  // RENDER
  // -------------------------

  const mediaPath = media?.file_path || "";
  const isImage = isImageFile(mediaPath);
  const isVideo = isVideoFile(mediaPath);
  const mediaUrl = mediaPath ? `/output/${mediaPath}` : "";

  // Auto-play the main video only while it is "in focus"
  useVideoAutoPlayOnFocus(finalVideoRef, isVideo, mediaPath);


  if (loading) {
    return (
      <div className="p-4 d-flex align-items-center gap-2">
        <div className="spinner-border" />
        <span>{t("common_loading_ellipsis")}</span>
      </div>
    );
  }

  if (err || !media) {
    return (
      <div className="p-4">
        <div className="alert alert-danger d-flex gap-2">
          <i className="bi bi-x-octagon-fill" />
          <span>{err || "Erreur chargement média"}</span>
        </div>
      </div>
    );
  }
  const toastIcon =
    toast?.type === "success"
      ? "bi-check-circle-fill"
      : toast?.type === "error"
        ? "bi-exclamation-octagon-fill"
        : "bi-info-circle-fill";

  return (
    <div className="md-wrap">
      <style>{legacyStyles}</style>

      {toast && (
        <div className={`md-toast md-toast-${toast.type}`}>
          <i className={`bi ${toastIcon}`} />
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} type="button">
            ×
          </button>
        </div>
      )}

      <section className="md-row">
        <main className="main-col">
          <article className="md-card">
            <div className="md-visual">

              {isImage && <img className="md-main" src={mediaUrl} alt="" />}
              {isVideo && (
                <video
                  ref={finalVideoRef}
                  className="md-main"
                  src={mediaUrl}
                  controls
                  playsInline
                />
              )}
            </div>

            {/* ------------------------- */}
            {/* TOOLBAR : LIKE + COMMENTS + SHARE + MENU */}
            {/* ------------------------- */}
            <div className="md-toolbar">
              <div className="md-toolbarLeft">
                {/* Contributors (real from DB + fake for demo) — moved under toolbar (left) */}
                {(() => {
                  const MAX_VISIBLE = 3;
                  const visible = (contributorsUi || []).slice(0, MAX_VISIBLE);
                  const more = Math.max(0, (contributorsUi || []).length - visible.length);

                  return (
                    <div className="md-toolbarContrib" aria-label={t("common_contributors")} title={t("common_contributors")}>
                      <div className="md-avatars md-avatars--toolbar">
                        {visible.map((c) => {
                          const img = (
                            <img
                              className="md-avatar"
                              src={c.avatar}
                              alt={c.label}
                              title={c.label}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = makeUiAvatar(c.fallback || c.label || "?");
                              }}
                            />
                          );

                          return c.to ? (
                            <Link
                              key={c.key}
                              to={c.to}
                              className="md-avatarLink"
                              aria-label={c.label}
                              title={c.label}
                            >
                              {img}
                            </Link>
                          ) : (
                            <span
                              key={c.key}
                              className="md-avatarLink"
                              aria-label={c.label}
                              title={c.label}
                            >
                              {img}
                            </span>
                          );
                        })}

                        {more > 0 && (
                          <span
                            className="md-avatarMore"
                            title={`${more} autre${more > 1 ? "s" : ""}`}
                            aria-label={`${more} autre${more > 1 ? "s" : ""}`}
                          >
                            +{more}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="md-toolbarRight">
                {/* LIKE */}
                <div
                  className="md-likeWrap"
                  onMouseEnter={() => {
                    setLikesPopoverOpen(true);
                    if ((likesUsers === null || likesUsersError) && !likesUsersLoading) {
                      fetchFinalLikesUsers();
                    }
                  }}
                  onMouseLeave={() => setLikesPopoverOpen(false)}
                >
                  <button
                    type="button"
                    className={`md-iconbtn md-like ${hasLiked ? "active" : ""}`}
                    onClick={handleToggleLike}
                    disabled={reactionsLoading}
                    title={t("common_like")}
                    aria-label={t("common_like")}
                  >
                    <i className={hasLiked ? "bi bi-heart-fill" : "bi bi-heart"}></i>
                    <span className="md-iconcount">{likesCount || 0}</span>
                  </button>

                  {likesPopoverOpen && (
                    <div className="md-likePopover" role="dialog" aria-label={t("common_liked_by")}>
                      {likesUsersLoading ? (
                        <div className="md-likeLoading">
                          <div className="spinner-border" role="status" aria-label={t("common_loading")}></div>
                          <span>{t("common_loading_ellipsis")}</span>
                        </div>
                      ) : likesUsersError ? (
                        <div className="md-likeError">
                          <div className="md-likeErrorText">{t("err_load_likes")}</div>
                          <button
                            type="button"
                            className="md-likeRetry"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              fetchFinalLikesUsers();
                            }}
                          >
                            {t("common_retry")}
                          </button>
                        </div>
                      ) : Array.isArray(likesUsers) && likesUsers.length > 0 ? (
                        <ul className="md-likeUsersList">
                          {likesUsers.slice(0, 24).map((u, idx) => {
                            const username = String(u?.username || u?.user || "").trim();
                            const label = String(
                              u?.displayName || u?.display_name || username || "?"
                            );

                            const apiAvatar = u?.avatarUrl || u?.avatar_url || null;
                            const src = normalizeUploadPath(apiAvatar) || avatarSrcForUsername(username);
                            const to = resolveUserProfilePath(username);

                            const avatarImg = (
                              <img
                                src={src}
                                alt={label}
                                title={label}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = makeUiAvatar(label || "?");
                                }}
                              />
                            );

                            return (
                              <li key={`${username || "u"}-${idx}`} className="md-likeUser">
                                {to ? (
                                  <Link to={to} className="md-likeUserLink" title={label}>
                                    <span className="md-likeAvatar">{avatarImg}</span>
                                    <span className="md-likeName">{label}</span>
                                  </Link>
                                ) : (
                                  <>
                                    <span className="md-likeAvatar">{avatarImg}</span>
                                    <span className="md-likeName">{label}</span>
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="md-likeEmpty">{t("common_no_likes_yet")}</div>
                      )}
                    </div>
                  )}
                </div>

                {/* COMMENTS */}
                <button
                  type="button"
                  className="md-iconbtn"
                  onClick={() => {
                    setCommentsExpanded(true);

                    // ✅ if you later add inline panels to final, keep this pattern
                    try {
                      if (window.location.hash) {
                        window.history.replaceState(null, "", window.location.pathname);
                      }
                    } catch { }

                    document
                      .getElementById("final-comments-anchor")
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  aria-label={t("common_comments")}
                  title={t("common_comments")}
                >
                  <i className="bi bi-chat"></i>
                  <span className="ms-1">{comments.length || 0}</span>
                </button>


                {/* SHARE */}
                <button
                  type="button"
                  className="md-iconbtn"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(window.location.href)
                      .then(() => showToast("success", t("toast_link_copied")))
                      .catch(() => showToast("error", t("toast_copy_failed")));
                  }}
                  aria-label={t("common_share")}
                  title={t("common_share")}
                >
                  <i className="bi bi-share"></i>
                </button>

                {/* MENU */}
                <div className="dropdown">
                  <button
                    className="md-iconbtn"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    title={t("common_actions")}
                  >
                    <i className="bi bi-three-dots"></i>
                  </button>

                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <a
                        className="dropdown-item"
                        href={media?.final_url || media?.url || media?.file_url || "#"}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ouvrir dans un nouvel onglet
                      </a>
                    </li>
                    <li>
                      <Link
                        className="dropdown-item"
                        to={`/projects/${projectId}/recap-ui`}
                      >
                        Voir le récap du projet
                      </Link>
                    </li>
                  </ul>
                </div>

              </div>
            </div>

            <header className="md-meta">
              <h2 className="md-title d-flex align-items-center gap-2">
                {/* ✅ creator avatar moved before title (same size as other avatars) */}
                {media.user && (
                  <span className="md-creatorSlot" aria-label="Créateur" title={media.user}>
                    {(() => {
                      const u = String(media.user || "").trim();
                      const src = avatarSrcForUsername(u);
                      const to = resolveUserProfilePath(u);

                      const img = (
                        <img
                          className="md-creatorAvatar"
                          src={src}
                          alt={u || "Créateur"}
                          title={u || "Créateur"}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = makeUiAvatar(u || "?");
                          }}
                        />
                      );

                      return to ? (
                        <Link
                          to={to}
                          className="md-creatorLink"
                          aria-label={u || "Créateur"}
                          title={u || "Créateur"}
                        >
                          {img}
                        </Link>
                      ) : (
                        img
                      );
                    })()}
                  </span>
                )}

                <span>{media.title}</span>

                {/* ✅ “final du projet” moved to the end */}
                <span className="badge bg-dark rounded-pill ms-1">final du projet</span>
              </h2>

              {media.description && <p className="md-desc">{media.description}</p>}

              <Link
                to={`/projects/${media.project_id}/recap-ui`}
                className="btn btn-outline-dark rounded-pill mb-3"
              >
                Voir le récap du projet →
              </Link>

              <div className="md-line">
                {/* ✅ creator avatar removed from meta line (now in title) */}
                {media.created_at && (
                  <span>
                    <i className="bi bi-calendar-event"></i>{" "}
                    {new Date(media.created_at).toLocaleDateString(locale)}
                  </span>
                )}

                <span>
                  <i className="bi bi-collection-play" /> {usedMedia.length} média(s)
                </span>
              </div>
            </header>


            {/* ------------------------- */}
            {/* COMMENTS SECTION */}
            {/* ------------------------- */}
            <section id="final-comments-anchor" className="md-comments mt-4">
              <button
                type="button"
                className="md-comments-toggle mb-3"
                onClick={() => setCommentsExpanded((v) => !v)}
              >
                <span className="md-comments-title">
                  {comments.length} commentaire{comments.length > 1 ? "s" : ""}
                </span>
                <i
                  className={`bi ${commentsExpanded ? "bi-chevron-up" : "bi-chevron-down"
                    }`}
                ></i>
              </button>

              {commentsExpanded && (
                <>
                  {/* Input */}
                  <div className="d-flex gap-2 mb-3">
                    <input
                      type="text"
                      className="form-control rounded-pill"
                      placeholder={t("common_add_comment_ph")}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill px-4"
                      onClick={submitComment}
                      disabled={commentLoading}
                    >
                      {commentLoading ? "…" : t("common_send")}
                    </button>
                  </div>

                  {/* List */}
                  <ul className="md-comments-list">
                    {comments.map((c) => {
                      const rawAuthor =
                        c?.author ||
                        c?.user ||
                        c?.username ||
                        c?.created_by ||
                        c?.createdBy ||
                        c?.owner ||
                        c?.uploader ||
                        "";
                      const author = String(rawAuthor || "").trim() || "?";
                      const src = avatarSrcForUsername(author);
                      const to = resolveUserProfilePath(author);
                      const createdAt = c?.createdAt || c?.created_at || c?.created;

                      const avatarImg = (
                        <img
                          src={src}
                          alt={author}
                          title={author}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = makeUiAvatar(author || "?");
                          }}
                        />
                      );

                      return (
                        <li key={c.id} className="md-comment">
                          <div className="md-comment-avatar md-comment-avatar--img">
                            {to ? (
                              <Link
                                to={to}
                                className="md-ownerLink"
                                aria-label={author}
                                title={author}
                              >
                                {avatarImg}
                              </Link>
                            ) : (
                              avatarImg
                            )}
                          </div>

                          <div className="md-comment-body">
                            <div className="md-comment-meta">
                              {to ? (
                                <Link
                                  to={to}
                                  className="md-comment-author md-comment-authorLink"
                                >
                                  {author}
                                </Link>
                              ) : (
                                <span className="md-comment-author">{author}</span>
                              )}

                              {createdAt && (
                                <span className="md-comment-date">
                                  {new Date(createdAt).toLocaleString(locale)}
                                </span>
                              )}
                            </div>

                            <p>{c.text}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
          </article>
        </main>

        {/* RIGHT RAIL */}
        <aside className="right-rail">
          <div className="rail-masonry">
            {sideList.map((m, idx) => (
              <FinalTile key={`R-${m.id || idx}`} item={m} />
            ))}
          </div>
        </aside>
      </section>

      <section className="md-bottom">
        <div className="bottom-masonry">
          {bottomList.map((m, idx) => (
            <FinalTile key={`B-${m.id || idx}`} item={m} />
          ))}
        </div>
      </section>
    </div>
  );
}


function FinalTile({ item }) {
  const { thumbUrl, isVid, isImg, to } = buildMediaThumbInfo(item);

  return (
    <Link to={to} className="pin">
      {isVid ? (
        <video className="pin-media" src={thumbUrl} muted playsInline />
      ) : isImg ? (
        <img
          className="pin-media"
          src={thumbUrl}
          alt={item.title || "media"}
          loading="lazy"
        />
      ) : (
        <div className="pin-media d-flex align-items-center justify-content-center">
          <i className="bi bi-file-earmark-text" />
        </div>
      )}
    </Link>
  );
}

function buildMediaThumbInfo(item) {
  const isFinal = item.__kind === "project-final";

  const fp = item.file_path || item.location || item.final_src || "";
  const srcBase = isFinal ? "/output/" : "/uploads/";
  const thumbUrl = `${srcBase}${fp}`;

  const ext = fp.split(".").pop()?.toLowerCase();
  const isVid = ["mp4", "webm", "mov", "ogg", "mkv"].includes(ext);
  const isImg = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

  const sourceKind = String(item?.sourceKind || item?.source || "").toUpperCase();
  const isProjectMedia =
    sourceKind === "PROJECT_MEDIA" ||
    sourceKind === "PROJECT" ||
    (!!item?.project_id && !isFinal);

  const to = isFinal
    ? `/mediaDetails/final-${item.projectId || item.project_id}`
    : isProjectMedia
      ? `/mediaDetails/pm-${item.id}`
      : `/mediaDetails/${item.id}`;

  return { thumbUrl, isVid, isImg, to };
}

/* ============================================================================
 * 4) COMPOSANT PRINCIPAL : ROUTAGE ENTRE NORMAL / FINAL
 * ========================================================================== */

export default function MediaDetails() {
  const { t, lang, locale } = useWeavevoI18n(MD_I18N, MD_LANG_KEY);

  const { id } = useParams();
  const isFinalRoute = String(id || "").startsWith("final-");

  return isFinalRoute ? (
    <FinalVideoDetails t={t} lang={lang} locale={locale} />
  ) : (
    <LegacyMediaDetails t={t} lang={lang} locale={locale} />
  );
}

